import { useState, useCallback, useRef } from "react";
import * as Sentry from "@sentry/nextjs";
import trackEvent from "services/mixpanel/mixpanel";
import { ClassRating, getRatingObjByValue } from "constants/rate-classes";
import {
  submitClassFeedback,
  updateClassFeedback
} from "services/api/class-feedback/classFeedbackService";
import {
  IRateClassDetailedFormValues,
  IRateClassInitialRatingFormValues,
  IUseRateClassModalProps,
  IUseRateClassModalReturnValues,
  RatingFormStep
} from "./use-rate-class-modal.types";
import { PendingClassRatingStatus } from "services/api/class-feedback/classFeedbackService.types";
import { userFeedbackEvents } from "services/mixpanel/user-feedback-events";

export const useRateClassModal = ({
  initialRating,
  primaryUser,
  metaData,
  data: { subject, liveClass, modalSource }
}: IUseRateClassModalProps): IUseRateClassModalReturnValues => {
  const [step, setStep] = useState(RatingFormStep.INITIAL_RATING);
  const [selectedRating, setSelectedRating] = useState<ClassRating | null>(
    initialRating
  );
  const userFeedbackIdRef = useRef<string>("");

  const handleRatingFormSubmit = async ({
    rating: classRating,
    entity_id
  }: IRateClassInitialRatingFormValues) => {
    try {
      const ratingObj = metaData.ratings.find(
        ({ rating }) => rating === classRating
      );
      if (!ratingObj) {
        throw new Error("Invalid rating selected");
      }
      const response = await submitClassFeedback(primaryUser.access_token, {
        rating: classRating,
        rating_id: ratingObj.rating_id,
        entity_id,
        status: PendingClassRatingStatus.COMPLETED
      });
      setSelectedRating(classRating);
      setStep(RatingFormStep.DETAILED_FEEDBACK);
      userFeedbackIdRef.current = response.user_feedback_id;

      trackEvent({
        eventName: userFeedbackEvents.liveClassFeedbackSubmitted,
        payload: {
          classId: liveClass.id,
          subject: subject.name,
          source: modalSource
        }
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const handleDetailedFormSubmit = async (
    data: IRateClassDetailedFormValues
  ) => {
    try {
      if (!userFeedbackIdRef.current) {
        throw new Error("Class rating not submitted first");
      }
      await updateClassFeedback(primaryUser.access_token, {
        user_feedback_id: userFeedbackIdRef.current,
        feedback_details: {
          ...data,
          status: PendingClassRatingStatus.COMPLETED
        }
      });
      setStep(RatingFormStep.SUBMITTED);
      trackEvent({
        eventName: userFeedbackEvents.liveClassDetailedFeedbackSubmit,
        payload: {
          feedbackFreeText: data.comment.length > 0,
          feedbackReasonCount: data.tags.length,
          feedbackType: getRatingObjByValue(selectedRating)?.key,
          incomingSource: modalSource,
          subject: subject.name,
          classID: liveClass.id
        }
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const handleDetailedFormSkip = useCallback(async () => {
    try {
      if (!userFeedbackIdRef.current) {
        throw new Error("Class rating not submitted first");
      }
      await updateClassFeedback(primaryUser.access_token, {
        user_feedback_id: userFeedbackIdRef.current,
        feedback_details: {
          tags: [],
          comment: "",
          status: PendingClassRatingStatus.SKIPPED
        }
      });
    } catch (error) {
      Sentry.captureException(error);
    }
    setStep(RatingFormStep.SUBMITTED);
  }, [primaryUser]);

  const handleRatingFormSkip = async (entity_id: string) => {
    try {
      await submitClassFeedback(primaryUser.access_token, {
        entity_id,
        status: PendingClassRatingStatus.DISMISSED
      });
      trackEvent({
        eventName: userFeedbackEvents.liveClassFeedbackDismissed,
        payload: {
          classId: liveClass.id,
          subject: subject.name,
          incomingSource: modalSource,
          source: "live class feedback bottom sheet"
        }
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  return {
    step,
    selectedRating,
    handleRatingFormSubmit,
    handleDetailedFormSkip,
    handleDetailedFormSubmit,
    handleRatingFormSkip
  };
};
